import { useState } from 'react'
import { useOrderPanelStore } from '../../stores/order-panel-store'
import { setOfferteStatus } from '../../lib/offertes'
import { OFFERTE_CLOSED_STATUS } from '../../lib/offerte-status'
import { findOffertePrimaryProduct, isCountBasedSku } from '../../lib/offerte-products'
import { calculateArea } from '../../lib/pricing/vloerkleed'
import { AfgezienDialog } from './AfgezienDialog'
import clsx from 'clsx'

export type AfhandelingMode = 'order' | 'quote' | 'afgezien'

/**
 * Afhandelingslaag van de native nieuwe-aanvraag — drie paden:
 *  1. Direct inboeken  → channel ORDER (converter-keten → WO + S01)
 *  2. Offerte per e-mail → channel QUOTE; de AfterSave-flow verstuurt de
 *     offerte-mail (PWA met pakketkeuze) automatisch zodra de producten
 *     compleet zijn — pakketkeuze is hier dus optioneel (klant kiest zelf).
 *  3. Klant heeft afgezien → registratie als QUOTE-lead die direct met de
 *     verloren-status ontstaat (leadStatus bij create voorkomt de mail-race)
 *     + reden via het geaudite set-status-pad.
 *
 * Telefoonscript-volgorde: ná de keuze rendert de pagina via `children` eerst
 * het gegevensformulier (nieuwe klant) en de planning (inboeken-pad); de
 * submit-CTA hieronder blijft gated op `customerReady`.
 */
export function AfhandelingBlok({
  mode,
  onModeChange,
  customerReady,
  customerHint,
  children,
}: {
  mode: AfhandelingMode | null
  onModeChange: (mode: AfhandelingMode) => void
  /** Klantgegevens compleet genoeg om te submitten (per pad bepaald door de pagina). */
  customerReady: boolean
  /** Uitleg waarom submit nog niet kan (bv. "vul naam + e-mail in"). */
  customerHint?: string
  /** Gegevensformulier + planning — gerenderd tussen de keuzekaarten en de submit-CTA. */
  children?: React.ReactNode
}) {
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const items = useOrderPanelStore((s) => s.items)
  const isSubmitting = useOrderPanelStore((s) => s.isSubmitting)
  const submitResult = useOrderPanelStore((s) => s.submitResult)
  const discountPercentage = useOrderPanelStore((s) => s.discountPercentage)
  const discountReason = useOrderPanelStore((s) => s.discountReason)
  const setChannel = useOrderPanelStore((s) => s.setChannel)
  const submitOrder = useOrderPanelStore((s) => s.submitOrder)

  const [afgezienOpen, setAfgezienOpen] = useState(false)
  const [afgezienBusy, setAfgezienBusy] = useState(false)

  // ── Validatie ─────────────────────────────────────────────
  const hasValidItem = items.some((item) => {
    if (isCountBasedSku(item.productCode)) return (item.aantal ?? 0) > 0
    return calculateArea(item).areaM2Rounded > 0
  })

  function pickMode(next: AfhandelingMode) {
    onModeChange(next)
    // Afgezien registreert als QUOTE (geen routes/planning); de mail wordt
    // onderdrukt via leadStatus bij submit. De reden-dialoog opent pas via de
    // CTA — eerst komen de (contact)gegevens.
    setChannel(next === 'order' ? 'ORDER' : 'QUOTE')
  }
  // Direct inboeken: per kleed mét pakketten is een pakketkeuze verplicht
  // (bij offerte/afgezien optioneel — klant kiest zelf via de offerte-mail).
  const itemsMissingPackage = items.filter((item) => {
    const product = findOffertePrimaryProduct(item.productCode)
    if (!product.hasPackages) return false
    if (isCountBasedSku(item.productCode)) return false
    if (calculateArea(item).areaM2Rounded <= 0) return false
    return !item.selectedPackage
  })
  const discountOk = discountPercentage === 0 || discountReason.length > 0

  const baseReady = !!bootstrap && hasValidItem && discountOk && !isSubmitting && !submitResult?.ok
    && customerReady
  const canSubmitOrder = baseReady && itemsMissingPackage.length === 0
  const canSubmitQuote = baseReady

  async function handleAfgezien(reason: string) {
    setAfgezienBusy(true)
    try {
      await submitOrder({ leadStatus: OFFERTE_CLOSED_STATUS })
      const result = useOrderPanelStore.getState().submitResult
      if (result?.ok && result.leadId) {
        // Status staat al goed (meegegeven bij create) — deze call legt de
        // reden vast en schrijft de audit-regel via het Brein-klare pad.
        try {
          await setOfferteStatus(result.leadId, OFFERTE_CLOSED_STATUS, reason)
        } catch (err) {
          console.error('[AfhandelingBlok] reden vastleggen mislukt:', err)
        }
      }
      if (result?.ok) setAfgezienOpen(false)
    } finally {
      setAfgezienBusy(false)
    }
  }

  const cards: Array<{
    key: AfhandelingMode
    title: string
    subtitle: string
    accent: string
    accentActive: string
  }> = [
    {
      key: 'order',
      title: 'Direct inboeken',
      subtitle: 'Klant gaat akkoord — order + ophaalafspraak direct aanmaken',
      accent: 'border-emerald-200 hover:border-emerald-400',
      accentActive: 'border-emerald-500 bg-emerald-50',
    },
    {
      key: 'quote',
      title: 'Offerte per e-mail',
      subtitle: 'Klant ontvangt de offerte en kiest zelf het pakket',
      accent: 'border-indigo-200 hover:border-indigo-400',
      accentActive: 'border-indigo-500 bg-indigo-50',
    },
    {
      key: 'afgezien',
      title: 'Klant heeft afgezien',
      subtitle: 'Registreer de aanvraag met reden — geen mail naar klant',
      accent: 'border-red-200 hover:border-red-400',
      accentActive: 'border-red-500 bg-red-50',
    },
  ]

  return (
    <div className="space-y-3">
      <label className="text-xs font-medium text-slate-600 block">Afhandeling</label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {cards.map((card) => (
          <button
            key={card.key}
            onClick={() => pickMode(card.key)}
            disabled={isSubmitting || !!submitResult?.ok}
            className={clsx(
              'text-left px-3 py-3 border rounded-lg transition-colors',
              mode === card.key ? card.accentActive : `bg-white ${card.accent}`
            )}
          >
            <div className="text-sm font-medium text-slate-900">{card.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{card.subtitle}</div>
          </button>
        ))}
      </div>

      {/* Gegevens (nieuwe klant) + planning (inboeken) — door de pagina aangeleverd */}
      {mode && children}

      {/* Validatie-feedback */}
      {mode && !customerReady && customerHint && (
        <p className="text-xs text-slate-500">{customerHint}</p>
      )}

      {/* Submit-CTA per pad */}
      {mode === 'order' && (
        <div className="space-y-2">
          {itemsMissingPackage.length > 0 && hasValidItem && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Kies eerst een pakket voor elk kleed ({itemsMissingPackage.length} zonder pakket) —
              bij direct inboeken is het pakket onderdeel van de order.
            </p>
          )}
          <button
            onClick={() => submitOrder()}
            disabled={!canSubmitOrder}
            className={clsx(
              'w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
              canSubmitOrder
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Bezig met inboeken...' : 'Inboeken'}
          </button>
        </div>
      )}

      {mode === 'quote' && (
        <button
          onClick={() => submitOrder()}
          disabled={!canSubmitQuote}
          className={clsx(
            'w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
            canSubmitQuote
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          )}
        >
          {isSubmitting ? 'Bezig...' : 'Offerte aanmaken en versturen'}
        </button>
      )}

      {mode === 'afgezien' && !submitResult?.ok && !afgezienOpen && (
        <button
          onClick={() => setAfgezienOpen(true)}
          disabled={!baseReady}
          className={clsx(
            'w-full px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
            baseReady
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          )}
        >
          Reden invullen en registreren
        </button>
      )}

      {!hasValidItem && mode && (
        <p className="text-xs text-slate-500">Vul eerst de afmetingen van minimaal één kleed in.</p>
      )}

      {/* Submit-fout (page-variant heeft geen drawer-banner) */}
      {submitResult && !submitResult.ok && (
        <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {submitResult.error || 'Er ging iets mis bij het aanmaken'}
        </div>
      )}

      <AfgezienDialog
        isOpen={afgezienOpen}
        isSubmitting={afgezienBusy || isSubmitting}
        onConfirm={handleAfgezien}
        onClose={() => setAfgezienOpen(false)}
      />
    </div>
  )
}
